import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  ArrowUpRight,
  Menu,
  X,
} from "lucide-react";
import BookingModal from "@/components/BookingModal";
import "@/App.css";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const locations = [
  {
    name: "Morden",
    address: "London Road, London, SM4 5HT",
    phone: "07438 480441",
    booking: "https://www.apptio.app/book/linh-dang-nails",
  },
  {
    name: "New Malden",
    address: "175 High Street, New Malden, KT3 4BH",
    phone: "07345 661802",
    booking: "https://www.apptio.app/book/angeline-nails-beauty-new-malden",
  },
  {
    name: "Worcester Park",
    address: "182 Central Road, Worcester Park, KT4 8HQ",
    phone: "020 8329 9125",
    booking: "https://www.apptio.app/book/angeline-nails-beauty-wp",
  },
];

const services = [
  {
    title: "Nail Care",
    desc: "Expert filing, shaping, cuticle care and an extensive range of lacquer shades and gel finishes.",
    img: "https://images.unsplash.com/photo-1632345031435-8727f6897d53?auto=format&fit=crop&q=80&w=800",
  },
  {
    title: "Manicure & Pedicure",
    desc: "Complete holistic treatment for a freshly manicured look and beautifully pedicured feet.",
    img: "https://images.unsplash.com/photo-1753285310651-6974a839c992?auto=format&fit=crop&q=80&w=800",
  },
  {
    title: "BIAB",
    subtitle: "Builder Gel in a Bottle",
    desc: "The ultimate nail strengthener. A natural, flexible overlay that promotes healthy nail growth while looking flawless.",
    img: "https://images.unsplash.com/photo-1610992015762-45dca7fa3a85?auto=format&fit=crop&q=80&w=800",
    featured: true,
  },
  {
    title: "Eyelash Extensions",
    desc: "Full, natural-looking lash extensions tailored to your desired look and eye shape.",
    img: "https://images.pexels.com/photos/5128220/pexels-photo-5128220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800",
  },
  {
    title: "Waxing",
    desc: "Smooth, gentle waxing treatments for a clean, long-lasting finish you'll love.",
    img: "https://images.unsplash.com/photo-1609840533741-62c180d0be79?auto=format&fit=crop&q=80&w=800",
  },
  {
    title: "O.P.I Dipping Powder",
    desc: "Lightweight, durable dipping powder for a stunning, chip-resistant manicure.",
    img: "https://images.unsplash.com/photo-1599948128020-9a44505b0d1b?auto=format&fit=crop&q=80&w=800",
  },
];

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "BIAB", href: "#biab" },
  { label: "Locations", href: "#locations" },
];

/* ───────── Header ───────── */
const Header = ({ onBookClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`header-glass fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "scrolled" : ""}`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
        <a
          href="#hero"
          className="font-heading text-2xl font-medium text-stone-900 tracking-tight"
          data-testid="logo-link"
        >
          Angeline
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="font-body text-sm font-medium tracking-wide text-stone-500 hover:text-stone-900 transition-colors duration-300 uppercase"
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={onBookClick}
            className="btn-book hidden md:inline-flex bg-stone-900 text-white font-body text-sm font-medium px-6 py-2.5 rounded-full hover:bg-stone-800 transition-colors duration-300"
            data-testid="header-book-button"
          >
            Book Now
          </button>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-stone-900 p-1"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="md:hidden mobile-menu-enter bg-white/95 backdrop-blur-xl border-t border-stone-100"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="block font-body text-base font-medium text-stone-700 hover:text-stone-900"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setMenuOpen(false);
                onBookClick();
              }}
              className="w-full bg-stone-900 text-white font-body text-sm font-medium px-6 py-3 rounded-full mt-2"
              data-testid="mobile-book-button"
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

/* ───────── Hero ───────── */
const Hero = ({ onBookClick }) => (
  <section
    id="hero"
    className="relative min-h-screen flex items-center justify-center"
    data-testid="hero-section"
  >
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1599206676335-193c82b13c9e?auto=format&fit=crop&q=80&w=1920&h=1080"
        alt="Professional manicure treatment at Angeline Nails"
        className="w-full h-full object-cover"
      />
      <div className="hero-overlay absolute inset-0" />
    </div>

    <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={stagger}
      >
        <motion.p
          variants={fadeInUp}
          className="font-body text-sm uppercase tracking-[0.25em] text-white/70 mb-6"
        >
          Nails & Beauty
        </motion.p>
        <motion.h1
          variants={fadeInUp}
          className="font-heading text-5xl md:text-7xl lg:text-8xl font-medium text-white tracking-tighter leading-[0.9]"
        >
          Angeline
        </motion.h1>
        <motion.p
          variants={fadeInUp}
          className="font-heading text-xl md:text-2xl text-white/85 mt-6 italic font-light tracking-wide"
        >
          Make every treatment unforgettable
        </motion.p>
        <motion.div variants={fadeInUp} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={onBookClick}
            className="btn-book bg-white text-stone-900 font-body text-sm font-medium px-8 py-3.5 rounded-full hover:bg-stone-100 transition-colors duration-300"
            data-testid="hero-book-button"
          >
            Book a Treatment
          </button>
          <span className="font-body text-xs uppercase tracking-[0.2em] text-white/50">
            Walk-ins Welcome
          </span>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ───────── About ───────── */
const About = () => (
  <section
    id="about"
    className="py-20 md:py-32 px-6 md:px-12"
    data-testid="about-section"
  >
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
      >
        <motion.p
          variants={fadeInUp}
          className="font-body text-sm uppercase tracking-[0.2em] text-stone-400 mb-4"
        >
          About Us
        </motion.p>
        <motion.h2
          variants={fadeInUp}
          className="font-heading text-3xl md:text-5xl font-medium text-stone-900 tracking-tight leading-tight"
        >
          High quality products,{" "}
          <span className="italic text-stone-500">guaranteed</span>
        </motion.h2>
        <motion.p
          variants={fadeInUp}
          className="font-body text-base md:text-lg text-stone-500 mt-8 leading-relaxed"
        >
          At Angeline Nails & Beauty, it's not just an ordinary nail salon. We pride
          ourselves on top quality products to ensure complete customer satisfaction.
          We use trusted brands such as Gelish, O.P.I and Shellac.
        </motion.p>
        <motion.p
          variants={fadeInUp}
          className="font-body text-base md:text-lg text-stone-500 mt-4 leading-relaxed"
        >
          Our staff are carefully selected to ensure a qualified and
          customer-oriented workforce. With competitive and affordable prices across
          all our locations, we look forward to welcoming you.
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative"
      >
        <div className="rounded-2xl overflow-hidden aspect-[4/5] lg:aspect-[3/4]">
          <img
            src="https://images.unsplash.com/photo-1652869111573-148ffc977b92?auto=format&fit=crop&q=80&w=700&h=900"
            alt="Nail polish collection at Angeline Nails salon"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute -bottom-6 -left-6 bg-stone-900 text-white px-6 py-4 rounded-xl hidden lg:block">
          <p className="font-heading text-2xl font-medium">3</p>
          <p className="font-body text-xs uppercase tracking-[0.15em] text-white/70 mt-0.5">
            Locations
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ───────── BIAB Feature ───────── */
const BiabFeature = ({ onBookClick }) => (
  <section
    id="biab"
    className="py-20 md:py-32 px-6 md:px-12 bg-[#F5E6E6]/30"
    data-testid="biab-section"
  >
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="order-2 lg:order-1"
      >
        <div className="rounded-2xl overflow-hidden aspect-square">
          <img
            src="https://images.unsplash.com/photo-1772983166346-93afb9898264?auto=format&fit=crop&q=80&w=900"
            alt="BIAB Builder Gel natural almond nails"
            className="w-full h-full object-cover"
          />
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="order-1 lg:order-2"
      >
        <motion.div variants={fadeInUp}>
          <span className="biab-badge inline-block font-body text-xs uppercase tracking-[0.2em] font-medium text-stone-700 px-4 py-1.5 rounded-full border border-stone-200">
            New Service
          </span>
        </motion.div>
        <motion.h2
          variants={fadeInUp}
          className="font-heading text-3xl md:text-5xl font-medium text-stone-900 tracking-tight leading-tight mt-6"
        >
          BIAB
        </motion.h2>
        <motion.p
          variants={fadeInUp}
          className="font-heading text-xl md:text-2xl italic text-stone-400 mt-2"
        >
          Builder Gel in a Bottle
        </motion.p>
        <motion.p
          variants={fadeInUp}
          className="font-body text-base md:text-lg text-stone-500 mt-6 leading-relaxed"
        >
          The ultimate nail strengthener. BIAB provides a natural, flexible overlay
          that promotes healthy nail growth while looking absolutely flawless. Perfect
          for those who want strong, beautiful nails with a natural finish.
        </motion.p>
        <motion.p
          variants={fadeInUp}
          className="font-body text-base md:text-lg text-stone-500 mt-4 leading-relaxed"
        >
          Available at all three of our locations. Book your BIAB treatment today and
          experience the difference.
        </motion.p>
        <motion.div variants={fadeInUp} className="mt-8">
          <button
            onClick={onBookClick}
            className="btn-book bg-stone-900 text-white font-body text-sm font-medium px-8 py-3.5 rounded-full hover:bg-stone-800 transition-colors duration-300 inline-flex items-center gap-2"
            data-testid="biab-book-button"
          >
            Book BIAB Treatment
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ───────── Services Grid ───────── */
const Services = () => (
  <section
    id="services"
    className="py-20 md:py-32 px-6 md:px-12"
    data-testid="services-section"
  >
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="mb-16"
      >
        <motion.p
          variants={fadeInUp}
          className="font-body text-sm uppercase tracking-[0.2em] text-stone-400 mb-4"
        >
          What We Offer
        </motion.p>
        <motion.h2
          variants={fadeInUp}
          className="font-heading text-3xl md:text-5xl font-medium text-stone-900 tracking-tight"
        >
          Our Services
        </motion.h2>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={stagger}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
      >
        {services
          .filter((s) => !s.featured)
          .map((s) => (
            <motion.div
              key={s.title}
              variants={fadeInUp}
              className="service-card rounded-2xl aspect-[4/5] relative flex items-end cursor-default"
              data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <img
                src={s.img}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover rounded-2xl"
              />
              <div className="card-content p-6 md:p-8 w-full">
                <h3 className="font-heading text-xl md:text-2xl font-medium text-white tracking-tight">
                  {s.title}
                </h3>
                <p className="font-body text-sm text-white/70 mt-2 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
      </motion.div>
    </div>
  </section>
);

/* ───────── Brand Marquee ───────── */
const BrandMarquee = () => (
  <section className="py-12 md:py-16 border-y border-stone-200" data-testid="brand-marquee">
    <Marquee speed={30} gradient={false} pauseOnHover>
      {["Gelish", "O.P.I", "Shellac", "BIAB", "Gelish", "O.P.I", "Shellac", "BIAB"].map(
        (brand, i) => (
          <span key={i} className="marquee-text mx-12 md:mx-20 select-none">
            {brand}
          </span>
        )
      )}
    </Marquee>
  </section>
);

/* ───────── Locations ───────── */
const Locations = () => (
  <section
    id="locations"
    className="py-20 md:py-32 px-6 md:px-12"
    data-testid="locations-section"
  >
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="mb-16"
      >
        <motion.p
          variants={fadeInUp}
          className="font-body text-sm uppercase tracking-[0.2em] text-stone-400 mb-4"
        >
          Visit Us
        </motion.p>
        <motion.h2
          variants={fadeInUp}
          className="font-heading text-3xl md:text-5xl font-medium text-stone-900 tracking-tight"
        >
          Our Locations
        </motion.h2>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={stagger}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        {locations.map((loc) => (
          <motion.div
            key={loc.name}
            variants={fadeInUp}
            className="location-card bg-white border border-stone-200 rounded-2xl p-8"
            data-testid={`location-card-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <h3 className="font-heading text-2xl font-medium text-stone-900 tracking-tight">
              {loc.name}
            </h3>

            <div className="mt-6 space-y-4">
              <div className="flex items-start gap-3">
                <MapPin
                  className="w-4 h-4 text-stone-400 mt-1 flex-shrink-0"
                  strokeWidth={1.5}
                />
                <p className="font-body text-sm text-stone-600 leading-relaxed">
                  {loc.address}
                </p>
              </div>

              {loc.phone && (
                <div className="flex items-center gap-3">
                  <Phone
                    className="w-4 h-4 text-stone-400 flex-shrink-0"
                    strokeWidth={1.5}
                  />
                  <a
                    href={`tel:${loc.phone.replace(/\s/g, "")}`}
                    className="font-body text-sm text-stone-600 hover:text-stone-900 transition-colors"
                    data-testid={`phone-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {loc.phone}
                  </a>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Clock
                  className="w-4 h-4 text-stone-400 mt-1 flex-shrink-0"
                  strokeWidth={1.5}
                />
                <div className="font-body text-sm text-stone-600">
                  <p>Mon - Sat: 10am - 7pm</p>
                  <p>Sun: 10am - 5pm</p>
                </div>
              </div>
            </div>

            <a
              href={loc.booking}
              data-testid={`book-online-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="btn-book mt-6 inline-flex items-center justify-center gap-2 w-full bg-stone-900 text-white font-body text-sm font-medium px-6 py-3 rounded-full hover:bg-stone-800 transition-colors duration-300"
            >
              Book Online
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
        className="mt-12 text-center"
      >
        <div className="inline-flex items-center gap-3 bg-stone-100 px-6 py-3 rounded-full">
          <Mail className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
          <a
            href="mailto:enquiry@angelinenails.co.uk"
            className="font-body text-sm text-stone-600 hover:text-stone-900 transition-colors"
            data-testid="contact-email"
          >
            enquiry@angelinenails.co.uk
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ───────── Footer ───────── */
const Footer = ({ onBookClick }) => (
  <footer className="bg-stone-900 text-white py-16 md:py-20 px-6 md:px-12" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
        {/* Brand */}
        <div className="md:col-span-2">
          <p className="font-heading text-3xl font-medium tracking-tight">
            Angeline
          </p>
          <p className="font-body text-sm text-stone-400 mt-3 max-w-sm leading-relaxed">
            Premium nails & beauty across three London & Surrey locations.
            Walk-ins always welcome.
          </p>
          <div className="flex items-center gap-4 mt-6">
            <a
              href="https://www.facebook.com/angelinenailskt4/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-stone-700 flex items-center justify-center hover:bg-stone-800 transition-colors duration-300"
              data-testid="footer-facebook"
              aria-label="Facebook"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Quick links */}
        <div>
          <p className="font-body text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
            Quick Links
          </p>
          <div className="space-y-3">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="block font-body text-sm text-stone-400 hover:text-white transition-colors duration-300"
                data-testid={`footer-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div>
          <p className="font-body text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
            Get in Touch
          </p>
          <div className="space-y-3">
            <a
              href="mailto:enquiry@angelinenails.co.uk"
              className="block font-body text-sm text-stone-400 hover:text-white transition-colors duration-300"
            >
              enquiry@angelinenails.co.uk
            </a>
            <p className="font-body text-sm text-stone-400">
              Mon - Sat: 10am - 7pm
            </p>
            <p className="font-body text-sm text-stone-400">
              Sun: 10am - 5pm
            </p>
          </div>
          <button
            onClick={onBookClick}
            className="btn-book mt-6 bg-white text-stone-900 font-body text-sm font-medium px-6 py-2.5 rounded-full hover:bg-stone-100 transition-colors duration-300"
            data-testid="footer-book-button"
          >
            Book Now
          </button>
        </div>
      </div>

      <div className="border-t border-stone-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="font-body text-xs text-stone-500">
          &copy; {new Date().getFullYear()} Angeline Nails & Beauty. All rights reserved.
        </p>
        <p className="font-body text-xs text-stone-600">
          United Kingdom
        </p>
      </div>
    </div>
  </footer>
);

/* ───────── App ───────── */
function App() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  return (
    <div className="App min-h-screen bg-[#FAFAF9]">
      <Header onBookClick={openBooking} />
      <Hero onBookClick={openBooking} />
      <About />
      <Services />
      <BiabFeature onBookClick={openBooking} />
      <BrandMarquee />
      <Locations />
      <Footer onBookClick={openBooking} />
      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>
  );
}

export default App;
